import { useEffect, useState } from 'react';
import { TrashIcon, PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import ConfirmModal from 'shared_items/ConfirmModal';
import {
  createCoreFramework,
  updateCoreFramework,
  deleteCoreFramework,
  uploadCoreFrameworkImage,
  deleteCoreFrameworkImage,
} from '../core_services';

const emptyForm = () => ({
  name: '',
  description: '',
});

const formFromFramework = (f) => ({
  name: f.name || '',
  description: f.description || '',
});

/**
 * Create/view/edit modal for a CoreFramework — a reusable visual frame the
 * user designs once and can later reference from any CoreCycle regardless
 * of horizon (see core module design notes, 2026-08-19).
 */
const ModalFrameworkForm = ({ framework, onClose, onSuccess, onDelete }) => {
  const isCreate = framework === null;
  const [isEditMode, setIsEditMode] = useState(isCreate);
  const [formData, setFormData] = useState(emptyForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [pendingImageFile, setPendingImageFile] = useState(null);

  useEffect(() => {
    setShowDeleteConfirm(false);
    setPendingImageFile(null);
    if (isCreate) {
      setIsEditMode(true);
      setFormData(emptyForm());
      setImagePreview(null);
    } else {
      setIsEditMode(false);
      setFormData(formFromFramework(framework));
      setImagePreview(framework.image_url || null);
    }
  }, [framework]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditToggle = () => {
    setFormData(formFromFramework(framework));
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isCreate) {
      onClose();
    } else {
      setFormData(formFromFramework(framework));
      setIsEditMode(false);
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const okTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!okTypes.includes(file.type)) {
      toast.error('Use JPEG, PNG or WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be at most 5 MB');
      return;
    }
    setPendingImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveImage = async () => {
    if (isCreate || !framework?.id) {
      setPendingImageFile(null);
      setImagePreview(null);
      return;
    }
    if (!framework.image_url && !framework.framework_image_object_path) {
      setPendingImageFile(null);
      setImagePreview(null);
      return;
    }
    setIsSubmitting(true);
    try {
      const updated = await deleteCoreFrameworkImage(framework.id);
      setPendingImageFile(null);
      setImagePreview(updated.image_url || null);
      toast.success('Image removed');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove image');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setIsSubmitting(true);
    try {
      let frameworkId = framework?.id;
      if (isCreate) {
        const response = await createCoreFramework(formData);
        frameworkId = response.data?.id;
        toast.success('Framework created successfully!');
      } else {
        await updateCoreFramework(framework.id, formData);
        toast.success('Framework updated successfully!');
      }
      if (pendingImageFile && frameworkId) {
        await uploadCoreFrameworkImage(frameworkId, pendingImageFile);
        toast.success('Image uploaded');
      }
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save framework');
      console.error('Failed to save core framework:', error.response?.data || error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(false);
    setIsSubmitting(true);
    try {
      await onDelete(framework.id);
      toast.success('Framework deleted successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to delete framework');
      console.error('Failed to delete core framework:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const headerTitle = isCreate ? 'Add Framework' : isEditMode ? 'Edit Framework' : 'Framework Details';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col z-10 mx-2 text-sm">

        <div className="flex items-center justify-between px-2 py-1 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-gray-800 pr-2 truncate leading-none">{headerTitle}</h2>
          <div className="flex items-center gap-0 flex-shrink-0">
            {!isCreate && !isEditMode && (
              <button type="button" onClick={handleEditToggle} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Edit">
                <PencilSquareIcon className="h-5 w-5" />
              </button>
            )}
            <button type="button" onClick={onClose} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-lg" title="Close">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {!isEditMode && !isCreate ? (
          <div className="flex-1 overflow-y-auto p-3 text-xs space-y-3">
            {framework.image_url && (
              <img src={framework.image_url} alt="" className="w-28 h-28 object-cover rounded-lg border border-gray-200" />
            )}
            <div>
              <span className="block font-medium text-gray-600">Name</span>
              <p className="text-gray-900 font-medium mt-0.5">{framework.name}</p>
            </div>
            <div>
              <span className="block font-medium text-gray-600">Description</span>
              <p className="text-gray-800 whitespace-pre-line mt-0.5">{framework.description?.trim() || 'No description'}</p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden min-h-0">
            <div className="flex-1 overflow-y-auto p-3 space-y-3 text-xs">
              <div>
                <label className="block font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  required
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
              <div>
                <label className="block font-medium text-gray-700 mb-1">Image</label>
                <div className="flex items-start gap-3">
                  <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 overflow-hidden flex items-center justify-center text-gray-400 text-[10px]">
                    {imagePreview ? (
                      <img src={imagePreview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      'No image'
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageFileChange}
                      className="text-xs max-w-[220px]"
                    />
                    <p className="text-[10px] text-gray-500">JPEG, PNG or WebP · max 5 MB</p>
                    {(imagePreview || pendingImageFile) && (
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="text-xs text-red-600 hover:underline text-left"
                      >
                        Remove image
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center gap-2 px-3 py-2 border-t border-gray-200 flex-shrink-0 bg-white">
              {!isCreate && (
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                  disabled={isSubmitting}
                >
                  Delete
                </button>
              )}
              <div className="flex gap-2 ml-auto">
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm bg-gray-700 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        )}

        {showDeleteConfirm && !isSubmitting && (
          <ConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
            title="Delete Framework"
            message={`Are you sure you want to delete "${framework?.name || formData.name}"? Cycles referencing it will keep their own data but lose the link.`}
            confirmText="Delete"
            cancelText="Cancel"
            overlayClassName="z-[70]"
          />
        )}
      </div>
    </div>
  );
};

export default ModalFrameworkForm;
